<script setup lang="ts">
defineProps<{
  icon: string;
  label: string;
  value: string;
  desc?: string;
  trend?: number;
  trendLabel?: string;
  accent?: boolean;
}>();
</script>

<template>
  <div class="kpi" :class="{ 'kpi--accent': accent }">
    <div class="kpi__top">
      <div class="kpi__icon-wrap">
        <i :class="`pi ${icon}`" class="kpi__icon" />
      </div>
      <div
        v-if="trend !== undefined && trend !== 0"
        class="kpi__trend"
        :class="trend >= 0 ? 'kpi__trend--up' : 'kpi__trend--down'"
      >
        <i
          :class="
            trend >= 0 ? 'pi pi-arrow-up-right' : 'pi pi-arrow-down-right'
          "
        />
        <span>{{ Math.abs(trend) }}%</span>
        <span v-if="trendLabel" class="kpi__trend-label">{{ trendLabel }}</span>
      </div>
    </div>
    <div class="kpi__body">
      <span class="kpi__label">{{ label }}</span>
      <span class="kpi__value">{{ value }}</span>
      <p v-if="desc" class="kpi__desc">{{ desc }}</p>
    </div>
  </div>
</template>

<style scoped>
/* Bento KPI card — référence: glass rounded-3xl p-6 refractive-border hover:scale-[1.02] */
.kpi {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.2s,
    box-shadow 0.2s;
  min-width: 0;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.kpi:hover {
  transform: scale(1.02);
  border-color: rgba(255, 214, 10, 0.4);
  box-shadow: 0 10px 30px -10px rgba(11, 19, 32, 0.08);
}
.kpi--accent {
  background: rgba(255, 255, 255, 0.85);
}

/* Icon + trend row */
.kpi__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.kpi__icon-wrap {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  background: rgba(255, 214, 10, 0.1);
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi__icon {
  font-size: 1.125rem;
  color: #705d00;
}

.kpi__trend {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
  width: fit-content;
  flex-shrink: 0;
}
.kpi__trend--up {
  color: #059669;
  background: rgba(5, 150, 105, 0.1);
}
.kpi__trend--down {
  color: #dc2626;
  background: rgba(220, 38, 38, 0.1);
}
.kpi__trend .pi {
  font-size: 0.5rem;
}
.kpi__trend-label {
  font-weight: 500;
  opacity: 0.75;
}

.kpi__body {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.kpi__label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #7f775f;
}

.kpi__value {
  font-size: clamp(1.5rem, 3.5vw, 1.875rem);
  font-weight: 800;
  line-height: 1.1;
  color: #0b1320;
  letter-spacing: -0.02em;
  margin-top: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi__desc {
  margin: 1rem 0 0;
  font-size: 0.625rem;
  line-height: 1.4;
  font-weight: 500;
  color: rgba(11, 19, 32, 0.4);
  white-space: normal;
  word-break: break-word;
}
</style>

<style>
[data-theme='dark'] .kpi {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .kpi:hover {
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .kpi--accent {
  background: rgba(25, 35, 50, 0.85);
}
[data-theme='dark'] .kpi__icon-wrap {
  background: rgba(255, 214, 10, 0.08);
}
[data-theme='dark'] .kpi__icon {
  color: #ffd60a;
}
[data-theme='dark'] .kpi__label {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .kpi__value {
  color: #f1f5f9;
}
[data-theme='dark'] .kpi__desc {
  color: rgba(255, 255, 255, 0.35);
}
</style>
